import { createContext, useContext, type ReactNode } from 'react';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, type ApiClient } from '@apis/contracts';

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Query defaults: queries retry only transient failures (network / 5xx, max 2); mutations
 * never retry automatically — business commands are re-sent only by the user, with the
 * same Idempotency-Key (spec §2).
 */
export function createAppQueryClient(onUnauthenticated: () => void): QueryClient {
  const handle = (error: unknown) => {
    if (isUnauthenticated(error)) onUnauthenticated();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: handle }),
    mutationCache: new MutationCache({ onError: handle }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (count, error) => {
          if (error instanceof ApiError && error.status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi must be used inside <ApiProvider>');
  return client;
}

/**
 * Refetch cadence. WebSocket hints make changes appear sooner, but delivery is never
 * guaranteed (spec §10), so every operational screen also polls.
 */
export const POLL = {
  kitchen: 10_000,
  default: 30_000,
} as const;
